import { z } from "zod";
import { defaultVatCode, isVatCode, vatCodeFromRate, vatRateFromCode, type VatCode } from "@/lib/vat";

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const shortText = z.string().trim().min(1).max(400);

const expenseCategories = [
  "material",
  "labor",
  "service",
  "permit",
  "equipment",
  "other"
] as const;

export const quoteLineItemTypes = [
  "material",
  "transport",
  "unloading",
  "pallet",
  "lintel",
  "accessory",
  "foam",
  "other"
] as const;

export const quoteStatuses = [
  "pending",
  "accepted",
  "rejected",
  "expired",
  "superseded",
  "withdrawn"
] as const;

export const proposalQuoteLineItemSchema = z.object({
  name: shortText,
  unit: z.string().trim().max(40).nullish(),
  quantity: z.number().finite().nullish(),
  unit_price_net: z.number().finite().nullish(),
  total_net: z.number().finite().nullish(),
  vat_rate: z.number().finite().min(0).max(100).nullish(),
  total_gross: z.number().finite().nullish(),
  item_type: z.enum(quoteLineItemTypes).nullish(),
  excluded_from_comparison: z.boolean().nullish(),
  sort_index: z.number().int().nullish()
});

export const proposalQuoteSchema = z.object({
  title: shortText,
  task_id: z.string().nullish(),
  contact_id: z.string().nullish(),
  contact_name: z.string().trim().max(200).nullish(),
  contact_email: z.string().trim().email().max(200).nullish(),
  quote_number: z.string().trim().max(120).nullish(),
  source_email_id: z.string().trim().max(200).nullish(),
  source_email_thread_id: z.string().trim().max(200).nullish(),
  variant_group: z.string().trim().max(120).nullish(),
  rfq_id: z.string().uuid().nullish(),
  amount: z.number().finite().nullish(),
  amount_net: z.number().finite().nullish(),
  vat_rate: z.number().finite().min(0).max(100).nullish(),
  amount_gross: z.number().finite().nullish(),
  shipping_cost: z.number().finite().nullish(),
  unloading_cost: z.number().finite().nullish(),
  pallet_deposit: z.number().finite().nullish(),
  pallet_amortization: z.number().finite().nullish(),
  comparable_total: z.number().finite().nullish(),
  is_comparable: z.boolean().nullish(),
  comparability_note: z.string().trim().max(1000).nullish(),
  currency: z.string().trim().max(8).nullish(),
  status: z.enum(quoteStatuses).nullish(),
  valid_until: isoDate.nullish(),
  delivery_window: z.string().trim().max(200).nullish(),
  notes: z.string().trim().max(4000).nullish(),
  file_path: z.string().trim().max(500).nullish(),
  file_name: z.string().trim().max(260).nullish(),
  extracted_text: z.string().max(200_000).nullish(),
  line_items: z.array(proposalQuoteLineItemSchema).max(200).nullish()
});

export const proposalQuoteUpdateSchema = z.object({
  quote_id: z.string().uuid(),
  status: z.enum(quoteStatuses).nullish(),
  valid_until: isoDate.nullish(),
  notes: z.string().trim().max(4000).nullish()
});

export const proposalBotExpenseSchema = z.object({
  title: shortText,
  amount: z.number().finite().positive().nullish(),
  amount_net: z.number().finite().nullish(),
  vat_rate: z.number().finite().min(0).max(100).nullish(),
  amount_gross: z.number().finite().nullish(),
  date: isoDate,
  category: z.enum(expenseCategories),
  phase_id: z.string().nullish(),
  task_id: z.string().nullish(),
  paid_by: z.string().trim().max(80).nullish(),
  notes: z.string().trim().max(2000).nullish(),
  supplier_contact_id: z.string().uuid().nullish(),
  source_email_id: z.string().trim().max(200).nullish(),
  invoice_number: z.string().trim().max(120).nullish(),
  quote_id: z.string().uuid().nullish(),
  supplier_name: z.string().trim().max(200).nullish(),
  vat_code: z.enum(["23", "19", "5", "0", "non_vat"]).nullish(),
  amount_basis: z.enum(["gross", "net", "black"]).nullish(),
  receipt_path: z.string().trim().max(500).nullish(),
  receipt_name: z.string().trim().max(260).nullish()
});

export const proposalContactSchema = z.object({
  name: shortText,
  email: z.string().trim().email().max(200).nullish(),
  phone: z.string().trim().max(60).nullish(),
  company: z.string().trim().max(200).nullish(),
  type: z.enum(["contractor", "supplier", "designer", "authority", "other"]).nullish(),
  trade: z.string().trim().max(120).nullish(),
  notes: z.string().trim().max(2000).nullish(),
  source_email: z.string().trim().email().max(200).nullish()
});

export type ProposalQuote = z.infer<typeof proposalQuoteSchema>;
export type ProposalQuoteUpdate = z.infer<typeof proposalQuoteUpdateSchema>;
export type ProposalBotExpense = z.infer<typeof proposalBotExpenseSchema>;
export type ProposalContact = z.infer<typeof proposalContactSchema>;
export type ProposalQuoteLineItem = z.infer<typeof proposalQuoteLineItemSchema>;

export const proposalSfrbMatchSchema = z.object({
  expense_id: z.string().uuid(),
  expense_title: z.string().trim().max(400).nullish(),
  line_no: z.number().int(),
  invoice_description: z.string().trim().min(1).max(2000),
  invoice_qty: z.number().finite().nullish(),
  invoice_unit: z.string().trim().max(40).nullish(),
  invoice_unit_price: z.number().finite().nullish(),
  invoice_amount: z.number().finite().nullish(),
  budget_code: z.string().trim().min(1).max(80),
  budget_item_id: z.string().uuid().nullish(),
  budget_qty: z.number().finite().nullish(),
  budget_unit: z.string().trim().max(40).nullish(),
  conversion_note: z.string().trim().max(1000).nullish(),
  confidence: z.number().min(0).max(1).nullish(),
  reason: z.string().trim().max(2000).default(""),
  source: z.enum(["ai", "dictionary", "memory"]),
  split_index: z.number().int().min(0).max(20).default(0)
});

export type ProposalSfrbMatch = z.infer<typeof proposalSfrbMatchSchema>;

export type BotProposalKind =
  | "quote"
  | "quote_update"
  | "expense"
  | "contact"
  | "task_update"
  | "document_classify"
  | "sfrb_match"
  | "email_draft"
  | "diary_entry";

export type ProposalEmailDraft = {
  quote_id?: string;
  to_address: string;
  to_name?: string | null;
  subject: string;
  body: string;
  lang?: "sk" | "hu" | "en";
  summary_hu?: string | null;
  purpose?: "rfq" | "follow_up" | "order" | "dozor" | "authority" | "other";
  contact_id?: string | null;
  offset_days?: number;
};

export type ProposalDiaryEntry = {
  log_date: string;
  work_summary: string;
  issues?: string | null;
  audio_document_id?: string | null;
  transcript_source?: "speech" | "manual";
};

export type ProposalTaskUpdate = {
  task_id: string;
  title?: string | null;
  budget_planned?: number | null;
  notes?: string | null;
};

export type ProposalDocumentClassify = {
  document_id: string;
  suggested_title: string;
  category?: string | null;
};
export type BotProposalStatus = "pending" | "applied" | "rejected";

/** F1.4 — tömeges DPH javítás (DB kind továbbra is `expense`). */
export type ExpenseVatBulkItem = {
  expense_id: string;
  vat_code: "23" | "19" | "5" | "0" | "non_vat";
  vat_rate: number;
  amount_net: number;
  vat_amount: number;
  amount_gross: number;
  title?: string;
};

export type BotProposalPayload =
  | { kind: "quote"; quote: ProposalQuote }
  | { kind: "quote_update"; quote_update: ProposalQuoteUpdate }
  | { kind: "expense"; expense: ProposalBotExpense }
  | { kind: "expense_vat_bulk"; expense_vat_bulk: { items: ExpenseVatBulkItem[] } }
  | { kind: "contact"; contact: ProposalContact }
  | { kind: "task_update"; task_update: ProposalTaskUpdate }
  | { kind: "document_classify"; document_classify: ProposalDocumentClassify }
  | { kind: "sfrb_match"; sfrb_match: ProposalSfrbMatch }
  | { kind: "email_draft"; email_draft: ProposalEmailDraft }
  | { kind: "diary_entry"; diary_entry: ProposalDiaryEntry };

/** Nettó / bruttó / összevethető összeg kiszámítása, ha hiányzik. */
export function enrichQuoteAmounts(q: ProposalQuote): ProposalQuote {
  const vat = q.vat_rate ?? 23;
  let amount_net = q.amount_net ?? null;
  let amount_gross = q.amount_gross ?? null;
  if (amount_net == null && amount_gross != null) {
    amount_net = Math.round((amount_gross / (1 + vat / 100)) * 100) / 100;
  }
  if (amount_gross == null && amount_net != null) {
    amount_gross = Math.round(amount_net * (1 + vat / 100) * 100) / 100;
  }
  if (amount_gross == null && q.amount != null) {
    amount_gross = q.amount;
    amount_net = Math.round((q.amount / (1 + vat / 100)) * 100) / 100;
  }
  const shipping = q.shipping_cost ?? 0;
  const unloading = q.unloading_cost ?? 0;
  const comparable =
    q.comparable_total ??
    (amount_gross != null ? Math.round((amount_gross + shipping + unloading) * 100) / 100 : null);

  return {
    ...q,
    vat_rate: vat,
    amount_net,
    amount_gross,
    amount: q.amount ?? amount_gross,
    comparable_total: comparable,
    is_comparable: q.is_comparable ?? true
  };
}

function expenseVatCode(e: ProposalBotExpense): VatCode {
  if (e.amount_basis === "black") return "non_vat";
  if (isVatCode(e.vat_code)) return e.vat_code;
  if (e.vat_rate != null) return vatCodeFromRate(e.vat_rate);
  return defaultVatCode({ category: e.category, title: e.title }) ?? "23";
}

export function enrichExpenseAmounts(e: ProposalBotExpense): ProposalBotExpense {
  const code = expenseVatCode(e);
  const vat = vatRateFromCode(code);
  let amount_net = e.amount_net ?? null;
  let amount_gross = e.amount_gross ?? e.amount ?? null;
  if (code === "non_vat" || code === "0" || vat === 0) {
    const gross = amount_gross ?? amount_net;
    return {
      ...e,
      vat_code: code,
      vat_rate: 0,
      amount_net: gross,
      amount_gross: gross,
      amount: e.amount ?? gross ?? undefined,
      amount_basis: e.amount_basis === "black" ? "black" : (e.amount_basis ?? "gross")
    };
  }
  if (amount_net == null && amount_gross != null) {
    amount_net = Math.round((amount_gross / (1 + vat / 100)) * 100) / 100;
  }
  if (amount_gross == null && amount_net != null) {
    amount_gross = Math.round(amount_net * (1 + vat / 100) * 100) / 100;
  }
  return {
    ...e,
    vat_code: code,
    vat_rate: vat,
    amount_net,
    amount_gross,
    amount: e.amount ?? amount_gross ?? undefined
  };
}
